import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import AnomalyService from "../../../../../core/service/AnomalyService";

/** @typedef {import('../types/anomalyTimeSeries.types').TimelineFilterState} TimelineFilterState */
/** @typedef {import('../types/anomalyTimeSeries.types').TimeSeriesResponse} TimeSeriesResponse */

const DEBOUNCE_MS = 300;

/**
 * @param {TimelineFilterState} filters
 * @param {(key: string) => string} t
 */
export const buildTimeSeriesParams = (filters) => {
  const metricId = String(filters.metricId || "").trim();
  if (!metricId) return null;

  /** @type {Record<string, string>} */
  const params = { metricId };

  if (filters.branchId)    params.branchId = String(filters.branchId);
  if (filters.datapointId) params.datapointId = String(filters.datapointId);
  if (filters.severity)    params.severity = filters.severity;
  if (filters.ruleCode)    params.ruleCode = filters.ruleCode;
  if (filters.status)      params.status = filters.status;

  if (filters.periodFrom) {
    params.periodFrom = moment(filters.periodFrom).format("YYYY-MM-DD");
  }
  if (filters.periodTo) {
    params.periodTo = moment(filters.periodTo).format("YYYY-MM-DD");
  }

  return params;
};

/**
 * @param {TimelineFilterState} filters
 * @param {(key: string) => string} t
 * @param {number|null} [refreshToken]
 */
export const useAnomalyTimeSeries = (filters, t, refreshToken = null) => {
  const [summary, setSummary] = useState(null);
  const [dataPoints, setDataPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  const fetchData = useCallback(() => {
    const params = buildTimeSeriesParams(filters);
    if (!params) {
      setSummary(null);
      setDataPoints([]);
      setHasLoaded(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    AnomalyService.getTimeSeries(params)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        /** @type {TimeSeriesResponse} */
        const body = res.data || {};
        setSummary(body.summary || null);
        setDataPoints(body.dataPoints || []);
        setHasLoaded(true);
        setLastFetchedAt(Date.now());
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setError(t("MetricAnomalyTimeline.loadError.label"));
        setSummary(null);
        setDataPoints([]);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }, [filters, t]);

  const retry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchData, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchData, refreshToken]);

  return {
    summary,
    dataPoints,
    isLoading,
    error,
    retry,
    lastFetchedAt,
    hasLoaded,
  };
};

export default useAnomalyTimeSeries;
